import { Helmet } from "react-helmet-async";
import { SITE_NAME, SITE_URL, SITE_PHONE } from "@/routes-data";
import { useLocale } from "@/i18n/LocaleContext";
import { alternatePaths, HREFLANG, HTML_LANG, OG_LOCALE, type Locale } from "@/i18n/locale";

export interface SeoProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  jsonLd?: object | object[];
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
}

export function Seo({
  title,
  description,
  path,
  image = "/og-default.svg",
  type = "website",
  jsonLd,
  noindex,
  publishedTime,
  modifiedTime,
  author,
}: SeoProps) {
  const { locale } = useLocale();
  const alts = alternatePaths(path);
  const url = `${SITE_URL}${alts[locale]}`;
  const fullImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  const fullTitle = title.includes(SITE_NAME)
    ? title
    : `${title} | ${SITE_NAME}`;
  const provided = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  // Sitewide LocalBusiness schema: ensure every page emits the business
  // entity exactly once (pages may still pass organizationJsonLd() themselves).
  const hasBusiness = provided.some(
    (d) => (d as { "@id"?: string })["@id"] === `${SITE_URL}/#business`,
  );
  const jsonLdArray = (hasBusiness ? provided : [organizationJsonLd(locale), ...provided]).map(
    (d) => localizeJsonLd(d, locale),
  );
  return (
    <>
    <Helmet>
      <html lang={HTML_LANG[locale]} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {!noindex && (
        <link rel="alternate" hrefLang={HREFLANG.en} href={`${SITE_URL}${alts.en}`} />
      )}
      {!noindex && (
        <link rel="alternate" hrefLang={HREFLANG.es} href={`${SITE_URL}${alts.es}`} />
      )}
      {!noindex && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${alts.en}`} />
      )}
      <link
        rel="alternate"
        type="application/rss+xml"
        title={locale === "es" ? "Blog de Axle Inc. (RSS)" : "Axle Inc. Blog (RSS)"}
        href={`${SITE_URL}${locale === "es" ? "/es" : ""}/rss.xml`}
      />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      )}
      <meta name="theme-color" content="#ff2800" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="geo.region" content="US-IN" />
      <meta name="geo.placename" content="Elkhart, Indiana" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={OG_LOCALE[locale]} />
      <meta property="og:locale:alternate" content={OG_LOCALE[locale === "es" ? "en" : "es"]} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} - ${title}`} />
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} - ${title}`} />

    </Helmet>
    {/* JSON-LD rendered outside Helmet (React 19 + helmet-async drops <script> children).
        Schema.org JSON-LD is valid anywhere in the document, including <body>. */}
    {jsonLdArray.map((data, i) => (
      <script
        key={i}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(data).replace(/</g, "\\u003c"),
        }}
      />
    ))}
    </>
  );
}

/**
 * Rewrites internal site URLs and inLanguage inside schema data so Spanish
 * pages reference /es/ counterparts. Entity @ids (#business, #website) stay
 * shared across languages. Pages keep passing English paths.
 */
/**
 * Spanish for the fixed English text emitted by the shared schema builders
 * below (pages pass their own already-translated names/descriptions).
 * Schema.org enum-like values (@type, dayOfWeek, contactType, availability,
 * availableLanguage) and entity ids are never translated.
 */
const SCHEMA_TEXT_ES: Record<string, string> = {
  "Axle Inc. is the area's exclusive Dexter Group distributor, providing trailer axles, hubs, brakes, suspension, and professional repair service to consumers and businesses across Elkhart County, Indiana.":
    "Axle Inc. es el distribuidor exclusivo de Dexter Group en la zona y ofrece ejes, mazas, frenos, suspensión y servicio profesional de reparación de remolques a consumidores y empresas en todo el condado de Elkhart, Indiana.",
  "The right axle. The right answer. The first time.": "El eje correcto. La respuesta correcta. A la primera.",
  "Trailer axles": "Ejes para remolque",
  "Dexter Group products": "Productos de Dexter Group",
  "Trailer brakes": "Frenos para remolque",
  "Wheel bearings": "Rodamientos de rueda",
  "Trailer suspension": "Suspensión para remolque",
  "Trailer repair": "Reparación de remolques",
  "Cash, Credit Card, Check": "Efectivo, tarjeta de crédito, cheque",
  "Trailer axles, parts and service in Elkhart, Indiana.": "Ejes, piezas y servicio para remolques en Elkhart, Indiana.",
  "Elkhart County, Indiana": "Condado de Elkhart, Indiana",
  "Trailer owners, fleet operators, dealerships": "Propietarios de remolques, operadores de flotas y concesionarios",
  "Trailer Parts": "Piezas para remolque",
  "Elkhart, Indiana": "Elkhart, Indiana",
};
const SCHEMA_ENUM_KEYS = new Set([
  "@type", "@id", "@context", "dayOfWeek", "contactType", "availability",
  "availableLanguage", "priceCurrency", "currenciesAccepted", "addressRegion",
  "addressCountry", "areaServed_code", "telephone", "email", "url", "opens", "closes",
]);

function localizeJsonLd<T>(data: T, locale: Locale): T {
  if (locale === "en") return data;
  const walk = (v: unknown, key?: string): unknown => {
    if (typeof v === "string") {
      if (key === "inLanguage") return HREFLANG[locale];
      if (!key || !SCHEMA_ENUM_KEYS.has(key)) {
        const es = SCHEMA_TEXT_ES[v];
        if (es !== undefined) return es;
      }
      if (key === "@id" && /#(business|website)$/.test(v)) return v;
      if (v.startsWith(SITE_URL) && !/\.[a-z0-9]{2,5}(\?|#|$)/i.test(v)) {
        const rest = v.slice(SITE_URL.length) || "/";
        if (!rest.startsWith("/")) return v;
        return `${SITE_URL}${alternatePaths(rest.replace(/[?#].*$/, ""))[locale]}${rest.match(/[?#].*$/)?.[0] ?? ""}`;
      }
      return v;
    }
    if (Array.isArray(v)) return v.map((x) => walk(x, key));
    if (v && typeof v === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, x] of Object.entries(v)) out[k] = walk(x, k);
      return out;
    }
    return v;
  };
  return walk(data) as T;
}

/**
 * Combined LocalBusiness + Organization for the site root identity.
 * Used on every page (via implicit reference) - emit on home page.
 */
export function organizationJsonLd(locale: Locale = "en") {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "AutoPartsStore", "AutoRepair"],
    "@id": `${SITE_URL}/#business`,
    name: SITE_NAME,
    legalName: "Axle Inc.",
    url: SITE_URL,
    telephone: SITE_PHONE,
    email: "info@axleinc.com",
    image: [`${SITE_URL}/og-default.svg`],
    logo: `${SITE_URL}/favicon.svg`,
    priceRange: "$$",
    foundingDate: "1995",
    currenciesAccepted: "USD",
    paymentAccepted: "Cash, Credit Card, Check",
    address: {
      "@type": "PostalAddress",
      streetAddress: "53664 Co Rd 9",
      addressLocality: "Elkhart",
      addressRegion: "IN",
      postalCode: "46514",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.6819,
      longitude: -85.9767,
    },
    brand: { "@type": "Brand", name: "Dexter" },
    areaServed: [
      { "@type": "City", name: "Elkhart", containedInPlace: { "@type": "State", name: "Indiana" } },
      { "@type": "City", name: "Granger" },
      { "@type": "City", name: "Mishawaka" },
      { "@type": "City", name: "Goshen" },
      { "@type": "City", name: "South Bend" },
      { "@type": "City", name: "Bristol" },
      { "@type": "City", name: "Middlebury" },
      { "@type": "City", name: "Nappanee" },
      { "@type": "City", name: "Wakarusa" },
      { "@type": "City", name: "New Paris" },
      { "@type": "City", name: "Plymouth" },
      { "@type": "City", name: "Warsaw" },
      { "@type": "City", name: "Osceola" },
      { "@type": "City", name: "Dunlap" },
      { "@type": "City", name: "Millersburg" },
      { "@type": "City", name: "Kendallville" },
      { "@type": "City", name: "Niles", containedInPlace: { "@type": "State", name: "Michigan" } },
      { "@type": "City", name: "Sturgis" },
      { "@type": "City", name: "Cassopolis" },
      { "@type": "City", name: "Benton Harbor" },
      { "@type": "City", name: "Kalamazoo" },
    ],
    inLanguage: HTML_LANG[locale],
    description: locale === "es"
      ? "Axle Inc. es el distribuidor exclusivo de Dexter Group en la zona y ofrece ejes, mazas, frenos, suspensión y servicio profesional de reparación de remolques a consumidores y empresas en todo el condado de Elkhart, Indiana."
      : "Axle Inc. is the area's exclusive Dexter Group distributor, providing trailer axles, hubs, brakes, suspension, and professional repair service to consumers and businesses across Elkhart County, Indiana.",
    slogan: locale === "es"
      ? "El eje correcto. La respuesta correcta. A la primera."
      : "The right axle. The right answer. The first time.",
    sameAs: [
      "https://parts.axleinc.com/",
      "https://axle.setmore.com/",
      "https://www.instagram.com/axleincin/",
      "https://www.facebook.com/AxleIncIN/",
      "https://www.youtube.com/channel/UCttD8joMoXxpRFcxYd-esuA",
      "https://www.linkedin.com/company/axle-inc",
    ],
    knowsAbout: [
      "Trailer axles",
      "Dexter Group products",
      "Trailer brakes",
      "Wheel bearings",
      "Trailer suspension",
      "Trailer repair",
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "17:00",
      },
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: SITE_PHONE,
        contactType: "sales",
        areaServed: "US-IN",
        availableLanguage: ["English"],
      },
      {
        "@type": "ContactPoint",
        telephone: SITE_PHONE,
        contactType: "customer service",
        areaServed: "US-IN",
        availableLanguage: ["English"],
      },
    ],
  };
}

/** WebSite schema with sitelinks searchbox potentialAction. */
export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    description: "Trailer axles, parts and service in Elkhart, Indiana.",
    publisher: { "@id": `${SITE_URL}/#business` },
    inLanguage: "en-US",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/blog/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function breadcrumbJsonLd(
  trail: { name: string; path: string }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export function serviceJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  serviceType?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}${opts.path}#service`,
    name: opts.name,
    serviceType: opts.serviceType ?? opts.name,
    description: opts.description,
    url: `${SITE_URL}${opts.path}`,
    provider: { "@id": `${SITE_URL}/#business` },
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Elkhart County, Indiana",
    },
    audience: {
      "@type": "Audience",
      audienceType: "Trailer owners, fleet operators, dealerships",
    },
  };
}

export function productCategoryJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  variants?: { name: string; desc: string }[];
}) {
  const base = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${SITE_URL}${opts.path}#product`,
    name: opts.name,
    description: opts.description,
    url: `${SITE_URL}${opts.path}`,
    brand: { "@type": "Brand", name: "Dexter Group" },
    category: "Trailer Parts",
    manufacturer: {
      "@type": "Organization",
      name: "Dexter Group",
      url: "https://www.dexter.com/",
    },
    seller: { "@id": `${SITE_URL}/#business` },
    offers: {
      "@type": "AggregateOffer",
      availability: "https://schema.org/InStock",
      priceCurrency: "USD",
      seller: { "@id": `${SITE_URL}/#business` },
      areaServed: "US-IN",
      url: "https://parts.axleinc.com/",
    },
  };
  if (opts.variants && opts.variants.length) {
    return [
      base,
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: opts.name,
        itemListElement: opts.variants.map((v, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: v.name,
          description: v.desc,
        })),
      },
    ];
  }
  return base;
}

export function collectionPageJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  items: { name: string; path: string; description?: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE_URL}${opts.path}#collection`,
    name: opts.name,
    description: opts.description,
    url: `${SITE_URL}${opts.path}`,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: opts.items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        url: `${SITE_URL}${it.path}`,
        ...(it.description ? { description: it.description } : {}),
      })),
    },
  };
}

/** Helpful for blog list pages. */
export function blogJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  posts: { title: string; slug: string; date: string; excerpt: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${SITE_URL}${opts.path}#blog`,
    name: opts.name,
    description: opts.description,
    url: `${SITE_URL}${opts.path}`,
    publisher: { "@id": `${SITE_URL}/#business` },
    inLanguage: "en-US",
    blogPost: opts.posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE_URL}/blog/${p.slug}/`,
      datePublished: p.date,
      description: p.excerpt,
    })),
  };
}

